/**
 * Adds an HTTPS protocol to bare external hosts while preserving URLs that
 * already have a scheme or intentionally inherit the current page protocol.
 */
export function normalizeExternalHref(href?: string): string | undefined {
  if (!href || href.startsWith('//') || /^[a-z][a-z0-9+.-]*:/i.test(href)) {
    return href
  }

  return `https://${href}`
}

/**
 * Normalizes an external web address while rejecting non-HTTP schemes.
 * Intended for untrusted API/crawler values that will be rendered as links.
 */
export function normalizeHttpHref(href?: string | null): string | undefined {
  const value = href?.trim()
  if (!value) return undefined
  if (value.startsWith('/') && !value.startsWith('//')) return undefined

  const explicitScheme = value.match(/^([a-z][a-z0-9+.-]*):/i)?.[1]?.toLowerCase()
  const hostWithPort = /^[^/]+:\d+(?:\/|$)/.test(value)
  const hasHttpProtocol = /^https?:\/\//i.test(value)
  if (explicitScheme && !hostWithPort && !hasHttpProtocol) {
    return undefined
  }

  const normalized = value.startsWith('//')
    ? `https:${value}`
    : hasHttpProtocol
      ? value
      : `https://${value}`

  try {
    const url = new URL(normalized)
    return (url.protocol === 'http:' || url.protocol === 'https:') && url.hostname
      ? normalized
      : undefined
  } catch {
    return undefined
  }
}
